import { cn } from '@/lib/cn';
import type { ArtifactKind } from '@innermate/shared';
import { Loader2, X } from 'lucide-react';
import { useEffect, useRef } from 'react';

export interface StreamingArtifactState {
  toolCallId: string;
  /** `system.create_artifact` | `system.update_artifact` */
  mode: 'create' | 'update';
  /** 지금까지 도달한 tool input JSON (partial). */
  partialJson: string;
  /** partial JSON 에서 추출된 값들. 아직 안 왔으면 undefined. */
  kind?: ArtifactKind;
  title?: string;
  content?: string;
  /**
   * true = 툴 실행 완료 (onArtifactCreated 도달), artifact 목록 refetch 대기 중.
   * 이 상태에서는 ArtifactPanel 이 우선 표시되며, 스트리밍 패널은 숨겨진다.
   */
  isDone?: boolean;
}

interface Props {
  streaming: StreamingArtifactState;
  onCancel?: () => void;
}

function streamingKindLabel(kind: ArtifactKind | undefined, mode: 'create' | 'update'): string {
  const verb = mode === 'create' ? '생성 중…' : '수정 중…';
  switch (kind) {
    case 'markdown':
      return `문서 ${verb}`;
    case 'html':
      return `페이지 ${verb}`;
    case 'svg':
      return `SVG ${verb}`;
    case 'mermaid':
      return `다이어그램 ${verb}`;
    case 'code':
      return `코드 ${verb}`;
    default:
      return verb;
  }
}

/**
 * LLM 이 `system.create_artifact` / `system.update_artifact` 의 input JSON 을 스트리밍하는
 * 동안 표시되는 패널. content 가 도착하는 대로 코드뷰로 라이브 표시.
 *
 * 완성되면 Session.tsx 가 이 컴포넌트 대신 실제 `ArtifactPanel` 로 스왑한다 (renderer view).
 */
export function StreamingArtifactPanel({ streaming, onCancel }: Props) {
  const preRef = useRef<HTMLPreElement>(null);

  // 스트리밍 중 새 줄이 붙으면 하단으로 자동 스크롤.
  // biome-ignore lint/correctness/useExhaustiveDependencies: content 변경 시마다 scroll
  useEffect(() => {
    const el = preRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [streaming.content]);

  const title = streaming.title ?? (streaming.mode === 'create' ? '생성 중…' : '수정 중…');
  const chars = streaming.content?.length ?? 0;

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-surface-muted">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-surface px-3">
        <span
          className={cn(
            'inline-flex h-5 shrink-0 items-center rounded px-1.5 text-[10px] uppercase tracking-wide',
            'bg-surface-muted font-mono text-foreground-muted',
          )}
        >
          {streaming.kind ?? '…'}
        </span>
        <h2 className="min-w-0 flex-1 truncate text-sm font-medium text-foreground" title={title}>
          {title}
        </h2>

        {!streaming.isDone && (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-surface-muted px-2 py-1 text-xs text-foreground-muted">
            <Loader2 className="size-3 animate-spin" />
            <span className="tabular-nums">{chars.toLocaleString()} chars</span>
          </span>
        )}

        {onCancel ? (
          <button
            type="button"
            aria-label="패널 닫기"
            onClick={onCancel}
            className="grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        ) : null}
      </header>

      <div className="relative flex-1 min-h-0 overflow-hidden bg-surface">
        {streaming.content && streaming.content.length > 0 ? (
          <pre
            ref={preRef}
            className="h-full w-full overflow-auto p-4 font-mono text-xs text-foreground/90 whitespace-pre"
          >
            {streaming.content}
          </pre>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-foreground-muted">
            <Loader2 className="mr-2 size-4 animate-spin" />
            {streaming.title
              ? `${streaming.title} ${streaming.mode === 'create' ? '생성 중…' : '수정 중…'}`
              : streamingKindLabel(streaming.kind, streaming.mode)}
          </div>
        )}
      </div>
    </div>
  );
}
